import { useSelector } from 'react-redux'
import { useParams } from 'react-router'

import { makeSelectFeatureSwitches } from '@webapp/core/stores/global/selectors'
import { useFeatureFlag } from '@webapp/hooks'
import { type TermsMethodsKindEnum } from '@webapp/modules/purchasing/entities/enums'

export type CustomizePurchaseOrderFormTab =
  | 'disclaimer'
  | 'billing-defaults'
  | TermsMethodsKindEnum
  | 'po-pdf-labels'
  | 'po-pdf-custom-fields'

/**
 * Orchestration hook for CustomizePurchaseOrderFormView.
 *
 * Owns the URL-derived tab state (`/settings/customfields-po/:tab?`, absent →
 * `disclaimer` — custom field management moved to the Main Custom Field Setup
 * page, PLAT-4936), the `enable_auto_po` gate for the External PO Numbering
 * card (BHV-003), and the `PUR_4706_AUTO_PO_BILL_TO` gate for the Billing
 * defaults tab. The two gates are independent (PUR-4708).
 */
export const useCustomizePurchaseOrderFormView = () => {
  const { tab } = useParams<{ tab?: CustomizePurchaseOrderFormTab }>()
  const activeTab: CustomizePurchaseOrderFormTab = tab ?? 'disclaimer'

  const { enable_auto_po } = useSelector(makeSelectFeatureSwitches())
  const PUR_4706_AUTO_PO_BILL_TO = useFeatureFlag('PUR_4706_AUTO_PO_BILL_TO')

  return {
    states: {
      activeTab,
      showExternalPONumbering: Boolean(enable_auto_po),
      showBillToSettings: PUR_4706_AUTO_PO_BILL_TO,
    },
  }
}
